import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, createBrowserRouter } from 'react-router';
import type { PermissionKey } from '@nexa/auth';
import { AppShell } from '@/components/app-shell';
import { Skeleton } from '@/components/ui/skeleton';
import { AccessBoundary, HomeRedirect, RequirePermission } from '@/auth/access-gate';
import { NAV_PERMISSIONS as NAV } from '@/lib/access/route-permissions';
import { HealthRoute } from './health';
import { DashboardRoute } from './dashboard/dashboard';
import { CasesRoute } from './cases/cases';
import { CasesHistoricRoute } from './cases/cases-historic';
import { CaseDetailRoute } from './case-detail/case-detail';
import { CaseNewRoute } from './cases/cases-new';
import { DisruptionsRoute } from './disruptions/disruptions';
import { DisruptionDetailRoute } from './disruptions/disruption-detail';
import { ToOperar } from './disruptions/to-operar';
import { TowerRoute } from './disruptions/tower/tower-route';
import { FlightsRoute } from './flights/flights';
import { PassengersRoute } from './passengers/passengers';
import { ManualReviewRoute } from './manual-review/manual-review';
import { ProvidersRoute } from './providers/providers';
// The policy screens (admin-only, RHF + zod + the 9-section config form, the Mapa,
// Simulador, Comparar and Asistente) are the heaviest non-chart surfaces; each is a
// lazy module in `policyRoutes` (policies-ux), so none lands in an operator's
// initial bundle — it downloads only when an admin opens /policies/*.
import { policyRoutes } from './policies/ux/policy-routes';
const ReservationsRoute = lazy(() =>
  import('./reservations/reservations').then((m) => ({ default: m.ReservationsRoute })),
);
// Declare-from-file (NEX-483 / I13). Lazy: the manifest preview pulls in the whole
// import surface and the global bundle sits ~20 KB under its cap, so this must not
// land in every operator's initial download just to sit unused.
const ImportsRoute = lazy(() =>
  import('./imports/imports-route').then((m) => ({ default: m.ImportsRoute })),
);
// Manifest formats (NEX-489 / I16) — the airline configuring its OWN file format, in
// its own app (manifest-ingestion §5 D7). Lazy for the same reason /imports is: the
// mapping grid, the JSON diff and the whole preview surface must not land in every
// operator's initial download just to sit unused.
const FormatsRoute = lazy(() =>
  import('./formats/formats-route').then((m) => ({ default: m.FormatsRoute })),
);
const NewFormatRoute = lazy(() =>
  import('./formats/new-format').then((m) => ({ default: m.NewFormatRoute })),
);
const FormatWorkspaceRoute = lazy(() =>
  import('./formats/format-workspace').then((m) => ({ default: m.FormatWorkspaceRoute })),
);
const FormatDetailRoute = lazy(() =>
  import('./formats/format-detail').then((m) => ({ default: m.FormatDetailRoute })),
);
// Express Mode (foolproof single-flight view) — lazy so the simplified surface
// stays out of the initial bundle like the policy editor does.
const ExpressRoute = lazy(() =>
  import('./express/express').then((m) => ({ default: m.ExpressRoute })),
);
// «Operar la disrupción» — the full-screen operating screen, Express's successor. Lazy:
// the views, panels and their copy must not land in every operator's initial download.
const OperarRoute = lazy(() =>
  import('./disruptions/operar/operar-route').then((m) => ({ default: m.OperarRoute })),
);
// Counter QR poster (passenger-profile-completion) — lazy: it pulls the QR
// encoder, which no other screen needs in the initial bundle.
const CountersQrRoute = lazy(() =>
  import('./counters/counters-qr').then((m) => ({ default: m.CountersQrRoute })),
);
// Bandeja de proveedor (sky-poc #810) — the rehearsal's simulated hotel/transport
// vendor. Lazy: a supervisor-only screen nobody opens outside a PoC run.
const ProviderSimRoute = lazy(() =>
  import('./provider-sim/provider-sim').then((m) => ({ default: m.ProviderSimRoute })),
);
import { ContractsRoute } from './contracts/contracts';
import { ContractNewRoute } from './contracts/contract-new';
import { ContractDetailRoute } from './contracts/contract-detail';
import { ProfileRoute } from './profile/profile';
// Users, roles & permissions (users-roles-permissions R6, brief §5 P1–P6) — lazy: admin
// screens most operators never open stay out of their initial download.
const UsersRoute = lazy(() => import('./users/users').then((m) => ({ default: m.UsersRoute })));
const UserDetailRoute = lazy(() =>
  import('./users/user-detail').then((m) => ({ default: m.UserDetailRoute })),
);
const RolesRoute = lazy(() => import('./roles/roles').then((m) => ({ default: m.RolesRoute })));
const RoleEditorRoute = lazy(() =>
  import('./roles/role-editor').then((m) => ({ default: m.RoleEditorRoute })),
);
const PermissionMatrixRoute = lazy(() =>
  import('./roles/permission-matrix').then((m) => ({ default: m.PermissionMatrixRoute })),
);
import { BillingRoute } from './billing/billing';
// Billing reports (billing-reports BU1): the statement per disruption, one line's
// evidence and an invoice by disruption — one lazy module, read by the few who reconcile.
const StatementRoute = lazy(() =>
  import('./billing/reports').then((m) => ({ default: m.StatementRoute })),
);
const LineEvidenceRoute = lazy(() =>
  import('./billing/reports').then((m) => ({ default: m.LineEvidenceRoute })),
);
const InvoiceBreakdownRoute = lazy(() =>
  import('./billing/reports').then((m) => ({ default: m.InvoiceBreakdownRoute })),
);
import { ReportsRoute } from './reports/reports';
import { LoginPage } from './login';
import { CallbackPage } from './callback';
import { ForbiddenPage } from './forbidden';
import { GoodbyePage } from './goodbye';

// Per docs/ui/auth0-frontend-tds.md §7.1, /login and /callback are SIBLINGS of
// the protected layout (AppShell). Wrapping the entire router in
// `withAuthenticationRequired` would make /callback inaccessible to an
// unauthenticated user mid-handshake.
// /forbidden and /goodbye are also siblings — both reached by users who are
// either not yet authenticated or just logged out.

/**
 * Gate a route on a permission (users-roles-permissions R5, brief §5 C1/P8): the screen,
 * or the in-place "Acceso denegado" — never a blank page. Sidebar surfaces (and their
 * sub-routes) use their nav item's view permission from `NAV_PERMISSIONS`, so a link and
 * its route cannot disagree; the few screens with no nav item use their catalog key.
 */
function guard(permission: PermissionKey, el: ReactNode): ReactNode {
  return <RequirePermission permission={permission}>{el}</RequirePermission>;
}

/** Wrap a lazily-loaded route element in a Suspense boundary with a skeleton. */
function lazyRoute(el: ReactNode): ReactNode {
  return (
    <Suspense
      fallback={
        <div className="p-6">
          <Skeleton className="h-48 w-full" />
        </div>
      }
    >
      {el}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/callback', element: <CallbackPage /> },
  { path: '/forbidden', element: <ForbiddenPage /> },
  { path: '/goodbye', element: <GoodbyePage /> },
  {
    path: '/',
    element: <AppShell />,
    children: [
      // Every console route renders through the access boundary (R5): held until access
      // is known, "Sin acceso asignado" for a member with no role, error state if the BFF
      // cannot say. /health and /profile need no permission (any signed-in member).
      {
        element: <AccessBoundary />,
        children: [
          { index: true, element: <HomeRedirect /> },
          { path: 'dashboard', element: guard(NAV.dashboard, <DashboardRoute />) },
          // /health stays as a developer aid (smoke route) per NEX-142.
          { path: 'health', element: <HealthRoute /> },
          // Cases list + historic archive per docs/ui/operator-ui-tds.md §4.1.
          // The historic route MUST come before the param route so React Router
          // doesn't match "historic" as a caseUrn.
          { path: 'cases', element: guard(NAV.cases, <CasesRoute />) },
          { path: 'cases/historic', element: guard(NAV.cases, <CasesHistoricRoute />) },
          // Manual ingestion wizard per docs/ui/operator-ui-tds.md §4.1 + §8.b3.
          // MUST come before /cases/:caseUrn so 'new' isn't matched as a URN.
          { path: 'cases/new', element: guard('cases.create', <CaseNewRoute />) },
          // Case-detail shell per docs/ui/operator-ui-tds.md §4.2.
          { path: 'cases/:caseUrn', element: guard(NAV.cases, <CaseDetailRoute />) },
          // Disruptions (root-cause aggregate) per docs/domains/disruption.tds.md §5/§6.
          // The detail param route comes after the list so the list path matches first.
          { path: 'disruptions', element: guard(NAV.disruptions, <DisruptionsRoute />) },
          // A disruption ALWAYS opens in Operar, the new UI (PO 2026-10-27): the bare path
          // redirects there (`ToOperar`; an old `#desk` deep link goes to /tower instead).
          // «Modo experto» — the Tower, the expert control room — lives under /tower; the
          // classic overview stays reachable under /classic.
          { path: 'disruptions/:disruptionUrn', element: guard(NAV.disruptions, <ToOperar />) },
          {
            path: 'disruptions/:disruptionUrn/tower',
            element: guard(NAV.disruptions, <TowerRoute />),
          },
          {
            path: 'disruptions/:disruptionUrn/classic',
            element: guard(NAV.disruptions, <DisruptionDetailRoute />),
          },
          // «Operar la disrupción» — one disruption, full screen: AppShell drops its top
          // bar and folds the sidebar into an icon rail on a desktop (none on a phone or
          // tablet) here (`useImmersiveMode`); the screen closes with its own ✕.
          {
            path: 'disruptions/:disruptionUrn/operar',
            element: guard(NAV.disruptions, lazyRoute(<OperarRoute />)),
          },
          // Express — declare by flight number. Its board (/express/:urn) is retired: the
          // declaration and every link to it land in Operar (PO 2026-10-27).
          { path: 'express', element: guard('disruptions.declare', lazyRoute(<ExpressRoute />)) },
          {
            path: 'express/:disruptionUrn',
            element: guard(NAV.disruptions, <ToOperar />),
          },
          // Flights monitor (NEX-342 / F4) — the operator's scoped current flights
          // with status + case marks + one-click create-case.
          { path: 'flights', element: guard(NAV.flights, <FlightsRoute />) },
          {
            path: 'reservations',
            element: guard(NAV.reservations, lazyRoute(<ReservationsRoute />)),
          },
          {
            path: 'reservations/:kind',
            element: guard(NAV.reservations, lazyRoute(<ReservationsRoute />)),
          },
          {
            path: 'reservations/:kind/:requestId',
            element: guard(NAV.reservations, lazyRoute(<ReservationsRoute />)),
          },
          // Passengers — the PNR / name / ticket search across the last 24 h as its
          // own surface (sidebar › Operation). Same component as the dashboard hero.
          { path: 'passengers', element: guard(NAV.passengers, <PassengersRoute />) },
          { path: 'counters/qr', element: guard(NAV.countersQr, lazyRoute(<CountersQrRoute />)) },
          // Declare a disruption from a manifest file (NEX-483 / I13). `/imports` is the
          // drop zone; `/imports/:importUrn` is one import — and that URL is also the
          // handoff link an operator sends an admin when a format is not configured yet,
          // so it must stay stable and addressable.
          { path: 'imports', element: guard('manifests.import', lazyRoute(<ImportsRoute />)) },
          {
            path: 'imports/:importUrn',
            element: guard('manifests.import', lazyRoute(<ImportsRoute />)),
          },
          // Manifest formats (NEX-489 / I16). Sits beside /imports because configuring a
          // format and using one are two halves of the same job.
          // `/new` and `/imports/*` MUST come before `/:formatUrn` so neither is matched
          // as a URN — the same ordering rule /policies and /contracts follow.
          { path: 'formats', element: guard(NAV.formats, lazyRoute(<FormatsRoute />)) },
          { path: 'formats/new', element: guard('formats.edit', lazyRoute(<NewFormatRoute />)) },
          {
            path: 'formats/imports/:importUrn',
            element: guard('formats.edit', lazyRoute(<FormatWorkspaceRoute />)),
          },
          {
            path: 'formats/:formatUrn',
            element: guard(NAV.formats, lazyRoute(<FormatDetailRoute />)),
          },
          // Manual review queue per docs/ui/operator-ui-tds.md §4.1 + §8 (c9, d4, j4).
          { path: 'manual-review', element: guard('manual_review.resolve', <ManualReviewRoute />) },
          // Provider status (live third-party vendor health) — diagnostics surface (D).
          { path: 'providers', element: guard(NAV.providers, <ProvidersRoute />) },
          // Provider tray (sky-poc #810): answer the manual provider's parked requests.
          {
            path: 'provider-sim',
            element: guard(NAV.providerSim, lazyRoute(<ProviderSimRoute />)),
          },
          // Policies per docs/ui/operator-ui-tds.md §4.1 + §8 (a1, a4, a5) — the whole
          // /policies table (Mapa, Simulador + the /explorer redirect, Comparar, Asistente,
          // detail) lives in `policies/ux/policy-routes.ts`, registered once (policies-ux
          // U0); later slices replace their module, never this file. Literal segments are
          // declared there before /:policyUrn so none is matched as a URN.
          ...policyRoutes({ guard, lazyRoute }),
          // Contracts per docs/ui/operator-ui-tds.md §4.1 + §8 (a8, a9).
          // /new MUST come before /:contractUrn so it isn't matched as a URN.
          { path: 'contracts', element: guard(NAV.contracts, <ContractsRoute />) },
          { path: 'contracts/new', element: guard('contracts.edit', <ContractNewRoute />) },
          {
            path: 'contracts/:contractUrn',
            element: guard(NAV.contracts, <ContractDetailRoute />),
          },
          // Thin admin / operator routes per docs/ui/operator-ui-tds.md §4.1 (NEX-170).
          // Users, roles & permissions (R6, brief §5). `/roles/matrix` and `/roles/new` MUST
          // come before `/roles/:key` so neither is matched as a role key. A user URN rides
          // the path encoded (`memberPath`).
          { path: 'users', element: guard(NAV.users, lazyRoute(<UsersRoute />)) },
          {
            path: 'users/:operatorUrn',
            element: guard(NAV.users, lazyRoute(<UserDetailRoute />)),
          },
          { path: 'roles', element: guard(NAV.roles, lazyRoute(<RolesRoute />)) },
          {
            path: 'roles/matrix',
            element: guard(NAV.roles, lazyRoute(<PermissionMatrixRoute />)),
          },
          {
            path: 'roles/new',
            element: guard('roles.manage', lazyRoute(<RoleEditorRoute mode="create" />)),
          },
          { path: 'roles/:key', element: guard(NAV.roles, lazyRoute(<RoleEditorRoute />)) },
          // Preferences + "Mi acceso" (P7) — any signed-in member, no permission gate.
          { path: 'profile', element: <ProfileRoute /> },
          { path: 'billing', element: guard(NAV.billing, <BillingRoute />) },
          // The breadcrumb's middle crumbs (Facturación › Disrupciones › …) link to these
          // segments; none is a screen of its own, so each lands back on /billing.
          ...['disruptions', 'lines', 'invoices'].map((s) => ({
            path: `billing/${s}`,
            element: <Navigate to="/billing" replace />,
          })),
          {
            path: 'billing/disruptions/:disruptionUrn',
            element: guard(NAV.billing, lazyRoute(<StatementRoute />)),
          },
          {
            path: 'billing/lines/:chargeUrn',
            element: guard(NAV.billing, lazyRoute(<LineEvidenceRoute />)),
          },
          {
            path: 'billing/invoices/:invoiceUrn',
            element: guard(NAV.billing, lazyRoute(<InvoiceBreakdownRoute />)),
          },
          // Reports per docs/ui/operator-ui-tds.md §4.1 + §8 (L1, L2, L3, L4).
          { path: 'reports', element: guard(NAV.reports, <ReportsRoute />) },
        ],
      },
    ],
  },
]);
